/**
 * Jungle ambience and music.
 *
 * Howard: "the game needs soothing music, sounds from the jungle."
 *
 * Both are generated live rather than streamed from files, so nothing extra downloads and
 * the music never loops audibly — it is a slow pentatonic wander that cannot land on a
 * wrong note, so it stays soothing however long you play.
 *
 * Everything reacts to the world: birds in the day, crickets at night, an owl now and
 * then after dark, and the whole bed ducks under rain.
 */

/** A gentle pentatonic scale — no semitone clashes, so any note fits any other. */
const SCALE = [0, 2, 4, 7, 9]
/** Root note, low enough to sit under the game rather than over it. */
const ROOT = 220

function midiToHz(semitonesFromRoot: number): number {
  return ROOT * Math.pow(2, semitonesFromRoot / 12)
}

export class Ambience {
  private readonly ctx: AudioContext
  private readonly musicBus: GainNode
  private readonly natureBus: GainNode

  private windSource: AudioBufferSourceNode | null = null
  private windGain: GainNode | null = null
  private cricketGain: GainNode | null = null

  private nextChord = 0
  private nextMelody = 0
  private nextBird = 0
  private nextOwl = 0
  private step = 0
  private random: () => number

  musicOn = true
  natureOn = true

  constructor(ctx: AudioContext, destination: AudioNode, seed = 424242) {
    this.ctx = ctx

    this.musicBus = ctx.createGain()
    // Deliberately far back in the mix. Music you notice is music you get sick of.
    this.musicBus.gain.value = 0.055
    this.musicBus.connect(destination)

    this.natureBus = ctx.createGain()
    this.natureBus.gain.value = 0.09
    this.natureBus.connect(destination)

    let a = seed >>> 0
    this.random = () => {
      a = (a + 0x6d2b79f5) >>> 0
      let t = Math.imul(a ^ (a >>> 15), 1 | a)
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }

    this.startWind()
    this.startCrickets()
  }

  setMusic(on: boolean): void {
    this.musicOn = on
    this.musicBus.gain.linearRampToValueAtTime(on ? 0.055 : 0, this.ctx.currentTime + 0.5)
  }

  setNature(on: boolean): void {
    this.natureOn = on
    this.natureBus.gain.linearRampToValueAtTime(on ? 0.09 : 0, this.ctx.currentTime + 0.5)
  }

  /** A soft wind bed under everything, always present. */
  private startWind(): void {
    const ctx = this.ctx
    const frames = ctx.sampleRate * 4
    const buffer = ctx.createBuffer(1, frames, ctx.sampleRate)
    const data = buffer.getChannelData(0)
    // Brown-ish noise: much gentler than white, which hisses.
    let last = 0
    for (let i = 0; i < frames; i++) {
      const white = Math.random() * 2 - 1
      last = (last + 0.02 * white) / 1.02
      data[i] = last * 3.5
    }
    const source = ctx.createBufferSource()
    source.buffer = buffer
    source.loop = true

    const filter = ctx.createBiquadFilter()
    filter.type = 'lowpass'
    filter.frequency.value = 420

    const gain = ctx.createGain()
    gain.gain.value = 0.5

    // Slowly breathe the wind in and out, so it never sits still.
    const lfo = ctx.createOscillator()
    const lfoGain = ctx.createGain()
    lfo.frequency.value = 0.045
    lfoGain.gain.value = 0.22
    lfo.connect(lfoGain).connect(gain.gain)
    lfo.start()

    source.connect(filter).connect(gain).connect(this.natureBus)
    source.start()
    this.windSource = source
    this.windGain = gain
  }

  /** Crickets — a fine shimmer that fades up at night. */
  private startCrickets(): void {
    const ctx = this.ctx
    const frames = ctx.sampleRate * 2
    const buffer = ctx.createBuffer(1, frames, ctx.sampleRate)
    const data = buffer.getChannelData(0)
    for (let i = 0; i < frames; i++) {
      // A pulsing band of noise, the way a chorus of crickets actually behaves.
      const pulse = Math.sin((i / ctx.sampleRate) * Math.PI * 2 * 22) > 0.4 ? 1 : 0.12
      data[i] = (Math.random() * 2 - 1) * pulse
    }
    const source = ctx.createBufferSource()
    source.buffer = buffer
    source.loop = true

    const filter = ctx.createBiquadFilter()
    filter.type = 'bandpass'
    filter.frequency.value = 4600
    filter.Q.value = 7

    const gain = ctx.createGain()
    gain.gain.value = 0

    source.connect(filter).connect(gain).connect(this.natureBus)
    source.start()
    this.cricketGain = gain
  }

  /** One soft note. The music is nothing but these, spaced generously. */
  private note(semitone: number, duration: number, gain: number, delay = 0): void {
    const ctx = this.ctx
    const start = ctx.currentTime + delay
    const osc = ctx.createOscillator()
    const env = ctx.createGain()
    const soften = ctx.createBiquadFilter()
    soften.type = 'lowpass'
    soften.frequency.value = 1400

    osc.type = 'sine'
    osc.frequency.value = midiToHz(semitone)
    env.gain.setValueAtTime(0, start)
    // Long swell and long tail — this is what makes it read as soothing rather than as
    // a game bleep.
    env.gain.linearRampToValueAtTime(gain, start + duration * 0.35)
    env.gain.exponentialRampToValueAtTime(0.0001, start + duration)

    osc.connect(soften).connect(env).connect(this.musicBus)
    osc.start(start)
    osc.stop(start + duration + 0.1)
  }

  /** A bird call: two or three quick rising blips. */
  private bird(): void {
    const base = 1500 + this.random() * 1400
    const count = 2 + Math.floor(this.random() * 2)
    for (let i = 0; i < count; i++) {
      const ctx = this.ctx
      const start = ctx.currentTime + i * (0.07 + this.random() * 0.06)
      const osc = ctx.createOscillator()
      const env = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.setValueAtTime(base, start)
      osc.frequency.exponentialRampToValueAtTime(base * (1.25 + this.random() * 0.4), start + 0.06)
      env.gain.setValueAtTime(0, start)
      env.gain.linearRampToValueAtTime(0.16, start + 0.012)
      env.gain.exponentialRampToValueAtTime(0.0001, start + 0.09)
      osc.connect(env).connect(this.natureBus)
      osc.start(start)
      osc.stop(start + 0.12)
    }
  }

  /** An owl: two low breathy notes, for after dark. */
  private owl(): void {
    const ctx = this.ctx
    for (const [delay, freq] of [[0, 420], [0.42, 380]] as const) {
      const start = ctx.currentTime + delay
      const osc = ctx.createOscillator()
      const env = ctx.createGain()
      const filter = ctx.createBiquadFilter()
      filter.type = 'lowpass'
      filter.frequency.value = 700
      osc.type = 'sine'
      osc.frequency.setValueAtTime(freq, start)
      osc.frequency.linearRampToValueAtTime(freq * 0.94, start + 0.3)
      env.gain.setValueAtTime(0, start)
      env.gain.linearRampToValueAtTime(0.12, start + 0.08)
      env.gain.exponentialRampToValueAtTime(0.0001, start + 0.34)
      osc.connect(filter).connect(env).connect(this.natureBus)
      osc.start(start)
      osc.stop(start + 0.4)
    }
  }

  /**
   * Advance. `daylight` is 0..1 from the day/night cycle and `wet` mutes the birds,
   * because birds go quiet in the rain.
   */
  update(dt: number, daylight: number, wet: boolean): void {
    const now = this.ctx.currentTime

    // Crickets swell as the light goes.
    if (this.cricketGain) {
      const target = (1 - daylight) * 0.5 * (wet ? 0.4 : 1)
      this.cricketGain.gain.linearRampToValueAtTime(target, now + 1.5)
    }
    // Wind picks up in the rain.
    if (this.windGain) {
      this.windGain.gain.linearRampToValueAtTime(wet ? 0.9 : 0.5, now + 2)
    }

    if (this.natureOn) {
      this.nextBird -= dt
      if (this.nextBird <= 0) {
        // Birds through the day, silent at night and quiet in the rain.
        if (daylight > 0.35 && !wet) this.bird()
        this.nextBird = 2.5 + this.random() * 7
      }
      this.nextOwl -= dt
      if (this.nextOwl <= 0) {
        if (daylight < 0.2) this.owl()
        this.nextOwl = 14 + this.random() * 26
      }
    }

    if (!this.musicOn) return

    // A slow chord every eight seconds or so, two notes, wide apart.
    this.nextChord -= dt
    if (this.nextChord <= 0) {
      this.step = (this.step + 1) % 4
      const root = SCALE[this.step % SCALE.length]! - 12
      this.note(root, 7, 0.5)
      this.note(root + SCALE[(this.step + 2) % SCALE.length]!, 6.5, 0.34, 0.3)
      this.nextChord = 7 + this.random() * 4
    }

    // And a single melody note now and then, drifting above it.
    this.nextMelody -= dt
    if (this.nextMelody <= 0) {
      const octave = this.random() > 0.55 ? 12 : 0
      this.note(SCALE[Math.floor(this.random() * SCALE.length)]! + octave, 3.4, 0.26)
      this.nextMelody = 2.6 + this.random() * 5
    }
  }

  dispose(): void {
    this.windSource?.stop()
  }
}
